import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// The desktop layout spans the nav content box; its groups spill past it into the nav padding first
const isOverflowing = (el: HTMLElement) => el.scrollWidth > el.clientWidth;

const DESKTOP_MUTATIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  characterData: true,
};

const isMeasurable = (nav: HTMLElement, desktop: HTMLElement) =>
  nav.clientWidth > 0 && getComputedStyle(desktop).display !== 'none';

/**
 * Collapses the desktop nav into the mobile layout when its content no longer fits on one line.
 * `desktopHiddenClassName` is the `nav` modifier that hides the desktop layout; it is lifted
 * synchronously while measuring a collapsed nav so no intermediate layout is rendered.
 * The nav is re-measured on every resize or desktop subtree change while it is measurable.
 */
export function useCollapseOnOverflow(
  navRef: RefObject<HTMLElement | null>,
  desktopRef: RefObject<HTMLElement | null>,
  desktopHiddenClassName: string,
): boolean {
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);

  const measure = useCallback(() => {
    const nav = navRef.current;
    const desktop = desktopRef.current;
    if (!nav || !desktop) return;

    const wasCollapsed = collapsedRef.current;
    if (wasCollapsed) nav.classList.remove(desktopHiddenClassName);
    const measurable = isMeasurable(nav, desktop);
    const overflowing = measurable && isOverflowing(desktop);
    if (wasCollapsed) nav.classList.add(desktopHiddenClassName);

    if (!measurable || overflowing === wasCollapsed) return;

    collapsedRef.current = overflowing;
    setCollapsed(overflowing);
  }, [navRef, desktopRef, desktopHiddenClassName]);

  useIsomorphicLayoutEffect(measure);

  useEffect(() => {
    const nav = navRef.current;
    const desktop = desktopRef.current;
    if (!nav || !desktop) return;

    const ResizeObserverCtor =
      typeof globalThis.ResizeObserver === 'function' ? globalThis.ResizeObserver : undefined;
    const resizeObserver = ResizeObserverCtor ? new ResizeObserverCtor(measure) : undefined;
    resizeObserver?.observe(nav);

    const MutationObserverCtor =
      typeof globalThis.MutationObserver === 'function' ? globalThis.MutationObserver : undefined;
    const mutationObserver = MutationObserverCtor ? new MutationObserverCtor(measure) : undefined;
    mutationObserver?.observe(desktop, DESKTOP_MUTATIONS);

    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [navRef, desktopRef, measure]);

  return collapsed;
}
